import { useValidators } from "@/composables/useValidators";
import type { GeneratorFieldConfig } from "@/signup/DynamicGeneratorField.vue";
import { computed, type Ref } from "vue";
import { Composer } from "vue-i18n";
import {
  CorrectionFactor,
  InvestmentSupport,
  MeteringLocation,
  SeGeneratorType,
  SeThermalSubtype,
} from "./enums";
import type { SeGoApplication } from "./types";

// Helper function to convert enum to options array
export const enumToOptions = (enumObj: { [key: string]: string }) => {
  return Object.values(enumObj).map((value) => ({
    value,
    label: value,
  }));
};

export function getGoBasicFields(
  t: Composer["t"],
  go_application: SeGoApplication
): GeneratorFieldConfig[] {
  const { required, requiredMinMax, exactDigitLength, exactStringLength } =
    useValidators(t);

  return [
    {
      title: "grid_area_id",
      model: "grid_area_id",
      type: "string",
      validation: [exactStringLength(3)],
    },
    {
      title: "grid_production_device_id",
      model: "grid_production_device_id",
      type: "string",
      validation: [exactDigitLength(18)],
    },
    {
      title: "grid_eidel_id",
      model: "grid_eidel_id",
      type: "string",
      validation: [exactDigitLength(5)],
    },
    {
      title: "grid_metered_by_hour",
      model: "grid_metered_by_hour",
      type: "boolean",
    },
    {
      title: "grid_concession",
      model: "grid_concession",
      type: "boolean",
    },
    {
      title: "metering_location",
      model: "metering_location",
      type: "dropdown",
      options: enumToOptions(MeteringLocation),
      validation: [required],
    },
    {
      title: "correction_factor_info",
      model: "correction_factor_info",
      type: "none",
      options: enumToOptions(CorrectionFactor),
      visible: computed(() => {
        return (
          go_application.metering_location !=
          MeteringLocation.DIRECTLY_BEFORE_GRID_CONNECTION
        );
      }),
    },
    {
      title: "holding_percentage",
      model: "holding_percentage",
      type: "number",
      validation: [requiredMinMax(0, 100)],
    },
  ];
}

export function getGeneratorFields(
  t: Composer["t"],
  unitTechnology?: Ref<SeGeneratorType | null>
) {
  const { required, requiredMin, reasonableDate, integer, min } =
    useValidators(t);

  const productionUnitFields: GeneratorFieldConfig[] = [
    {
      title: "technology",
      model: "technology",
      type: "dropdown",
      options: enumToOptions(SeGeneratorType),
      validation: [required],
    },
    {
      title: "device_name",
      model: "device_name",
      type: "string",
      validation: [required],
    },
    {
      title: "device_power",
      model: "device_power",
      type: "number",
      validation: [required, integer, min(0)],
    },
    {
      title: "commissioning_date",
      model: "commissioning_date",
      type: "date",
      validation: [required, reasonableDate],
    },
    {
      title: "property_designation",
      model: "property_designation",
      type: "string",
      validation: [required],
    },
    {
      title: "postal_code",
      model: "postal_code",
      type: "string",
      validation: [required],
    },
    {
      title: "city",
      model: "city",
      type: "string",
      validation: [required],
    },
  ];

  const thermalFields: GeneratorFieldConfig[] = [
    {
      title: "thermal_subtype",
      model: "thermal_subtype",
      type: "dropdown",
      options: enumToOptions(SeThermalSubtype),
      validation: unitTechnology
        ? computed(() =>
            unitTechnology.value === SeGeneratorType.THERMAL ? [required] : []
          )
        : [],
      visible: unitTechnology
        ? computed(() => unitTechnology.value === SeGeneratorType.THERMAL)
        : undefined,
    },
    {
      title: "thermal_is_chp",
      model: "thermal_is_chp",
      type: "checkbox",
      visible: unitTechnology
        ? computed(() => unitTechnology.value === SeGeneratorType.THERMAL)
        : undefined,
    },
  ];

  const investmentFields: GeneratorFieldConfig[] = [
    {
      title: "investment_support_amount",
      model: "investment_support_amount",
      type: "number",
      validation: [requiredMin(0)],
    },
    {
      title: "investment_support_date",
      model: "investment_support_date",
      type: "date",
      validation: [required],
    },
  ];

  const investmentSupportOptions = Object.values(InvestmentSupport).map(
    (value) => ({
      value,
      label: `signup.se.generator_review.investment_support_options.${value
        .toLowerCase()
        .replace(/ /g, "_")}`,
    })
  );

  return {
    productionUnitFields,
    thermalFields,
    investmentFields,
    investmentSupportOptions,
  };
}
